import type { BoardRoute } from './types';

/**
 * The one place a route's colour is decided.
 *
 * Colour is the vehicle's identity in the incumbent: the same hue marks it in
 * the vehicle list, on the route row and on the line drawn across the map, and
 * a dispatcher learns "Allan is the blue one". That only works if every screen
 * agrees, so both the board and the map ask this rather than each keeping a
 * palette and an index of its own -- which they did, and which meant a route
 * could be one colour on the timeline and another on the map as soon as the
 * two lists were ordered differently.
 *
 * Keyed to the vehicle's own number, not to a position in the day's list: a van
 * that finishes early and drops out would otherwise recolour the whole fleet.
 */

/** Distinguishable at small sizes and colourblind-safe enough for 20 routes. */
export const ROUTE_COLORS = [
  '#1f6fb2', '#b4531a', '#6a4bb5', '#1c7f4d', '#b32d6b',
  '#2b7fa8', '#8a6d10', '#177f74', '#9c4a34', '#5c7d18',
  '#7a3fae', '#b06b1f', '#1a7fa0', '#a83a55', '#4a7c2a',
  '#6b52b0', '#a8761a', '#158069', '#a63b3b', '#3f6ea8',
];

/**
 * `fallbackIndex` is the row position, used only for a vehicle with no number
 * of its own -- an import that predates the column. It is never preferred over
 * the vehicle's number.
 */
export function routeColor(route: BoardRoute, fallbackIndex: number): string {
  if (route.vehicleColor) return route.vehicleColor;
  const n = route.vehicleIdx ?? fallbackIndex + 1;
  return ROUTE_COLORS[(n - 1) % ROUTE_COLORS.length] as string;
}
